import type { Ref, WritableComputedRef } from 'vue'
import type { AppTextareaEvent, NativeSelectOption } from '@shared/ui/fields'
import type { DisciplineFilePackDetails, DisciplineFilePackPayload } from './interfaces'

import { useListsStore } from '@shared/model'

import { DisciplineFilesModal } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineFilesStore } from './files/store'
import { useDisciplineFilesApi } from '../api/files.hook'
import { useDisciplineFilePackShowApi, useDisciplineFilePackUpdateApi } from '../api/file-pack.hook'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import {
  buildPackForm,
  getPackCategories,
  getTaskOptionText,
  isPackFormChanged,
} from './discipline-files'
import { formatTaskDate } from './tutor-tasks/tutor-tasks'

type DisciplineFilePackForm = Required<DisciplineFilePackPayload>

interface ModalDisciplineFilePackReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refPack: Ref<DisciplineFilePackDetails | null>
  refForm: Ref<DisciplineFilePackForm | null>
  refTutorId: WritableComputedRef<number | null>
  refTitle: ComputedRef<string>
  refLastUpdate: ComputedRef<string>
  refCategoryOptions: ComputedRef<NativeSelectOption[]>
  refTaskOptions: ComputedRef<NativeSelectOption[]>
  refPackTaskText: ComputedRef<string | null>
  refIsChanged: ComputedRef<boolean>
  canManageFiles: ComputedRef<boolean>
  setCategory: (option: NativeSelectOption) => void
  setTask: (option: NativeSelectOption) => void
  setComment: (event: AppTextareaEvent) => void
  closeModal: () => void
  savePack: () => Promise<void>
}

const NO_DATE = '---'

export const useModalDisciplineFilePack = (): ModalDisciplineFilePackReturn => {
  const route = useRoute()
  const disciplineStore = useDisciplineStore()
  const store = useDisciplineFilesStore()
  const listsStore = useListsStore()
  const { canManageFiles } = useDisciplinePrivileges()

  const refIsLoading = ref(false)
  const refPack = ref<DisciplineFilePackDetails | null>(null)
  const refForm = ref<DisciplineFilePackForm | null>(null)

  const disciplineId = (): string => route.params.id as string

  const refIsOpen = computed<boolean>(() => store.getActiveModal === DisciplineFilesModal.Pack)

  const refTutorId = computed<number | null>({
    get: () => refForm.value?.tutor_id ?? null,
    set: (value) => {
      if (refForm.value) refForm.value.tutor_id = value
    },
  })

  const refTitle = computed<string>(() => {
    const kid = disciplineStore.getDiscipline?.kid
    return `Files from: ${kid?.name ?? ''} ${kid?.grade ? `(${kid.grade})` : ''}`.trim()
  })

  const refLastUpdate = computed<string>(() => formatTaskDate(refPack.value?.updated_at ?? null) || NO_DATE)

  const refCategoryOptions = computed<NativeSelectOption[]>(() => {
    const pack = refPack.value
    if (!pack) return []

    return Object.entries(getPackCategories(pack.owner_type, listsStore.getLists?.file ?? null)).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refForm.value?.category,
    }))
  })

  const refTaskOptions = computed<NativeSelectOption[]>(() =>
    store.getTasks.map(task => ({
      id: task.id,
      value: task.id,
      text: getTaskOptionText(task),
      selected: task.id === refForm.value?.task_id,
    })))

  /** A pack that already holds a task shows it read-only, as the legacy form did. */
  const refPackTaskText = computed<string | null>(() => {
    const task = refPack.value?.task
    return task ? `#${task.id} ${formatTaskDate(task.created_at)}` : null
  })

  const refIsChanged = computed<boolean>(() =>
    !!refPack.value && !!refForm.value && isPackFormChanged(refPack.value, refForm.value))

  const setCategory = (option: NativeSelectOption): void => {
    if (refForm.value) refForm.value.category = String(option.value)
  }

  const setTask = (option: NativeSelectOption): void => {
    if (refForm.value) refForm.value.task_id = option.value === null ? null : Number(option.value)
  }

  const setComment = (event: AppTextareaEvent): void => {
    if (refForm.value) refForm.value.comment = event.value
  }

  const closeModal = (): void => {
    if (refIsLoading.value) return
    store.closeModal()
  }

  const loadPack = async (packId: number): Promise<void> => {
    refPack.value = null
    refForm.value = null

    const pack = await useDisciplineFilePackShowApi({
      disciplineId: disciplineId(),
      packId,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (!pack) {
      store.closeModal()
      return
    }

    refPack.value = pack
    refForm.value = buildPackForm(pack)
  }

  const savePack = async (): Promise<void> => {
    const pack = refPack.value
    const form = refForm.value
    if (!pack || !form || !refIsChanged.value || refIsLoading.value) return

    const updated = await useDisciplineFilePackUpdateApi({
      disciplineId: disciplineId(),
      packId: pack.id,
      payload: {
        category: form.category,
        comment: form.comment || null,
        tutor_id: form.tutor_id,
        ...(form.task_id && { task_id: form.task_id }),
      },
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    if (!updated) return

    store.closeModal()
    await useDisciplineFilesApi({ disciplineId: disciplineId() })
  }

  watch(refIsOpen, (isOpen) => {
    const packId = store.getActivePackId
    if (isOpen && packId) void loadPack(packId)
  })

  return {
    refIsOpen,
    refIsLoading,
    refPack,
    refForm,
    refTutorId,
    refTitle,
    refLastUpdate,
    refCategoryOptions,
    refTaskOptions,
    refPackTaskText,
    refIsChanged,
    canManageFiles,
    setCategory,
    setTask,
    setComment,
    closeModal,
    savePack,
  }
}
